import { useMemo, forwardRef } from 'react';
import { Input, InputProps, InputRef } from 'antd';
import classNames from 'classnames';

import { ReactComponent as CloseIcon } from '@Icons/additional/close-cross.svg';
import { ReactComponent as EyeIcon } from '@Icons/additional/password-eye.svg';
import { ReactComponent as OffEyeIcon } from '@Icons/additional/password-eye-off.svg';
import { ReactComponent as SearchIcon } from '@Icons/additional/search.svg';

import styles from './input.module.css';

interface MInputProps extends InputProps {
  searchInput?: boolean;
  sizeSearchIcon?: 'small' | 'middle' | 'large' | number;
  handleSubmit?: (value: string) => void;
}

const renderPasswordIcon = (visible: boolean): JSX.Element => {
  return visible ? <EyeIcon /> : <OffEyeIcon />;
};

const MInput = forwardRef<InputRef, MInputProps>((props, ref) => {
  const {
    searchInput = false,
    sizeSearchIcon = 16,
    className,
    type,
    handleSubmit,
    value,
    allowClear,
    ...restProps
  } = props;

  const submitHandler = (): void => {
    handleSubmit?.(value as string);
  };

  const sizeOfIcon = useMemo(() => {
    switch (sizeSearchIcon) {
      case 'small':
        return { width: '12px' };
      case 'middle':
        return { width: '16px' };
      case 'large':
        return { width: '24px' };
      default:
        return { width: `${sizeSearchIcon}px` };
    }
  }, [sizeSearchIcon]);

  if (type === 'password') {
    return (
      <Input.Password
        ref={ref}
        {...restProps}
        value={value}
        className={classNames('trojan-input', className)}
        iconRender={renderPasswordIcon}
        onPressEnter={submitHandler}
      />
    );
  }

  return (
    <Input
      ref={ref}
      prefix={
        searchInput && (
          <SearchIcon
            className={classNames(
              styles.searchIcon,
              handleSubmit ? styles.searchIconClickable : null
            )}
            style={sizeOfIcon}
            onClick={submitHandler}
          />
        )
      }
      {...restProps}
      value={value}
      className={classNames('trojan-input', className)}
      onPressEnter={submitHandler}
      allowClear={
        allowClear && {
          clearIcon: <CloseIcon className={styles.searchCloseIcon} />,
        }
      }
    />
  );
});

export default MInput;
